<script setup lang="ts">
import { inject, computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useOpsStore } from '@/stores/ops'
import { useAssignmentsStore } from '@/stores/assignments'
import { useTeamsStore } from '@/stores/teams'
import { useFormsStore } from '@/stores/forms'
import { useAuthStore } from '@/stores/auth'
import { useSyncStore } from '@/stores/sync'
import { useToastStore } from '@/stores/toast'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import FosButton from '@/components/ui/FosButton.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'
import type { TeamAssignment } from '@/types'

const opsStore = useOpsStore()
const assignmentsStore = useAssignmentsStore()
const teamsStore = useTeamsStore()
const formsStore = useFormsStore()
const authStore = useAuthStore()
const syncStore = useSyncStore()
const toast = useToastStore()
const { opRole } = useCurrentOpRole()
const router = useRouter()
const incidentId = inject<string>('incidentId', '')

watch(
  () => opsStore.selectedOpId,
  (opId) => {
    if (incidentId && opId) assignmentsStore.subscribeToAssignments(incidentId, opId)
  },
  { immediate: true },
)

const debriefItems = computed(() =>
  assignmentsStore.teamAssignments.map((ta) => {
    const assignment = assignmentsStore.assignments.find((a) => a.assignment_id === ta.assignment_id)
    const team = teamsStore.teams.find((t) => t.team_id === ta.team_id)
    return { ta, assignment, team }
  }).filter((item) => item.assignment && item.team),
)

/** A debrief counts as started if the SAR 110 form doc has been saved OR the
 * quick debrief modal has written debrief_cols on the team assignment. */
function isStarted(ta: TeamAssignment): boolean {
  if (formsStore.forms[`SAR110_${ta.ta_id}`]?.last_updated_at) return true
  return Object.keys(ta.debrief_cols ?? {}).length > 0
}

function statusText(ta: TeamAssignment): string {
  return isStarted(ta) ? 'Debriefed' : 'Not started'
}

function navigate(taId: string) {
  router.push({ name: 'incident-form-sar110', params: { id: incidentId, taId } })
}

const exporting = ref(false)

async function exportAll() {
  if (!syncStore.isOnline) {
    toast.add('PDF export requires a network connection.', 'warning')
    return
  }
  const opId = opsStore.selectedOpId
  if (!opId) return
  exporting.value = true
  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch(
      `/api/incidents/${incidentId}/operational_periods/${opId}/debriefs/export-pdf`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
    )
    const body = await res.json()
    if (res.status === 403) {
      toast.add("You don't have permission to perform this action.", 'error')
      return
    }
    if (!res.ok || body.error) {
      throw new Error(body.detail ?? body.error?.message ?? 'Export failed')
    }
    window.open(body.data.pdf_url, '_blank')
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Export failed'
    toast.add(message, 'error')
  } finally {
    exporting.value = false
  }
}
</script>

<template>
  <div class="h-full grid" style="grid-template-rows: 48px 1fr; min-height: 0;">
    <!-- Sub-toolbar -->
    <div class="flex items-center gap-2.5 px-4 bg-surface border-b border-border shrink-0">
      <button
        class="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0"
        @click="router.push({ name: 'incident-forms', params: { id: incidentId } })"
      >
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        Forms
      </button>
      <span class="w-px h-[18px] bg-border shrink-0" />
      <span class="font-headline font-black text-[13px] uppercase tracking-[0.04em] text-on-surface">SAR 110 · Team Debriefs</span>
      <span class="flex-1" />

      <!-- Bulk export -->
      <PermissionGuard action="forms.export_pdf" :role="opRole">
        <FosButton
          variant="secondary"
          size="sm"
          :disabled="exporting || debriefItems.length === 0"
          @click="exportAll"
        >
          <span v-if="exporting" class="w-3 h-3 border-2 border-outline/30 border-t-outline animate-spin inline-block mr-1" />
          <span v-else class="material-symbols-outlined text-[14px] mr-1">picture_as_pdf</span>
          {{ exporting ? 'Generating…' : 'Export All PDF' }}
        </FosButton>
      </PermissionGuard>
    </div>

    <!-- Content -->
    <div class="overflow-y-auto min-h-0">
      <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
        <!-- Section head -->
        <div class="flex flex-col gap-0.5">
          <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">SAR 110 · Team Debrief</span>
          <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Team Debriefs</h2>
          <p class="text-[13px] text-outline m-0">One form per team assignment in the current operational period.</p>
        </div>

        <!-- Empty state -->
        <div
          v-if="debriefItems.length === 0"
          class="bg-surface border border-border rounded p-10 text-center"
        >
          <p class="font-mono text-[12px] text-outline">No team assignments in this operational period yet.</p>
        </div>

        <!-- Debrief cards -->
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button
            v-for="{ ta, assignment, team } in debriefItems"
            :key="ta.ta_id"
            type="button"
            class="text-left bg-surface border border-border rounded flex flex-col hover:bg-surface-container-low transition-colors focus:outline-none focus:ring-1 focus:ring-primary/30"
            @click="navigate(ta.ta_id)"
          >
            <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
              <span class="font-mono font-semibold text-[11px] tracking-[0.06em] text-on-surface">{{ team!.team_identifier }}</span>
              <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">SAR 110</span>
            </div>
            <div class="px-3.5 py-2.5 flex flex-col gap-1">
              <p class="font-body text-[12px] text-outline leading-snug">{{ assignment!.assignment_name }}</p>
              <p
                class="font-mono text-[10px] tracking-[0.06em] uppercase"
                :class="isStarted(ta) ? 'text-primary' : 'text-outline'"
              >
                {{ statusText(ta) }}
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
